import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Layers, Sparkles, Plus, Trash2, Radio, CheckCircle } from "lucide-react";
import DateField from "@/components/app/DateField";

export default function JornadaRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselTeam = searchParams.get("team");
  const editId = searchParams.get("edit");

  const [teams, setTeams] = useState([]);
  const [cats, setCats] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(preselTeam || "");
  const [categoryTemplate, setCategoryTemplate] = useState(null);
  const [porteroTemplate, setPorteroTemplate] = useState(null);
  const [players, setPlayers] = useState([]);
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [activePlayer, setActivePlayer] = useState(null);
  const [activeSeason, setActiveSeason] = useState(null); // {label,start_date,end_date}

  const [form, setForm] = useState({
    session_type: "partido",
    date: new Date().toISOString().slice(0, 10),
    opponent: "",
    home_away: "casa",
    jornada_number: 1,
    notes: "",
    team_stats: {
      gols_favor: 0, gols_contra: 0, gols_propia: 0,
      xuts_favor: 0, xuts_contra: 0, parades_porter: 0,
      targetes_grogues: 0, targetes_roges: 0, resultado: "E",
      corners_favor: 0, corners_contra: 0, tiros_bloqueados: 0,
      duelos_aereos_ganados: 0, duelos_aereos_perdidos: 0,
      pases_completados: 0, pases_intentados: 0,
    },
    substitutions: [], // [{minute, out_player_id, in_player_id}]
    status: "final", // "draft" while the match is ongoing, "final" once the coach closes it
  });
  const [playerStats, setPlayerStats] = useState({}); // {playerId: {...}}
  const [rotate, setRotate] = useState(false);

  // Load teams
  useEffect(() => {
    (async () => {
      try {
        const [t, c] = await Promise.all([api.get("/teams"), api.get("/categories")]);
        setTeams(t.data);
        setCats(c.data);
        // Only auto-select teams[0] for NEW jornadas. When editing we let the edit-effect
        // set selectedTeam from the loaded jornada (may not be teams[0]).
        if (!editId) {
          setSelectedTeam(prev => prev || t.data[0]?.id || "");
        }
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [editId]);

  // Load active season (if defined) and auto-align the jornada date to it on NEW jornadas.
  useEffect(() => {
    if (editId) return; // don't override the date when editing an existing jornada
    (async () => {
      try {
        const { data } = await api.get("/seasons");
        const active = (data || []).find(s => s.is_active);
        if (!active) return;
        setActiveSeason(active);
        // If today is outside the active season window, default to the season start.
        const today = new Date().toISOString().slice(0, 10);
        if (today < active.start_date || today > active.end_date) {
          setForm(f => ({ ...f, date: active.start_date }));
        }
      } catch { /* club may not have seasons defined yet — non-blocking */ }
    })();
  }, [editId]);

  // Load template + players when team changes
  useEffect(() => {
    if (!selectedTeam) return;
    (async () => {
      try {
        const team = teams.find(x => x.id === selectedTeam);
        if (!team) return;
        const cat = cats.find(c => c.id === team.category_id);
        const tplKey = cat?.template_key || "U12";
        const [tpl, tplPor, pl] = await Promise.all([
          api.get(`/templates/${tplKey}?team_id=${selectedTeam}`),
          api.get(`/templates/PORTERO?team_id=${selectedTeam}`),
          api.get(`/players?team_id=${selectedTeam}`),
        ]);
        setCategoryTemplate(tpl.data);
        setPorteroTemplate(tplPor.data);
        setPlayers(pl.data);
        // Merge defaults with any values that might already exist (e.g. loaded from the jornada
        // when re-opening a draft). Using functional setState so we don't clobber async race.
        setPlayerStats((prev) => {
          const next = {};
          pl.data.forEach((p) => {
            const existing = prev[p.id];
            next[p.id] = existing || {
              player_id: p.id, convocado: true, minutos: 0, gols: 0, asistencias: 0,
              targetes_grogues: 0, targetes_roges: 0, parades: 0,
              shots_on_goal: 0,
              faltas_cometidas: 0, faltas_recibidas: 0,
              ratings: [], observaciones: "",
            };
          });
          return next;
        });
        // Expand first block of both templates
        const exp = {};
        (tpl.data.blocks || []).forEach((b, i) => { exp[b.id] = i === 0; });
        (tplPor.data.blocks || []).forEach((b, i) => { exp[b.id] = i === 0; });
        setExpandedBlocks(exp);
        if (pl.data.length > 0) setActivePlayer(pl.data[0].id);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [selectedTeam, teams, cats]);

  // Load existing jornada if editing
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/jornadas/${editId}`);
        setSelectedTeam(data.team_id);
        setForm({
          session_type: data.session_type,
          date: data.date,
          opponent: data.opponent || "",
          home_away: data.home_away || "casa",
          jornada_number: data.jornada_number || 1,
          notes: data.notes || "",
          team_stats: data.team_stats || form.team_stats,
          substitutions: data.substitutions || [],
          status: data.status || "final",
        });
        const psMap = {};
        (data.player_stats || []).forEach(ps => { psMap[ps.player_id] = ps; });
        setPlayerStats(psMap);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [editId]);

  const currentTeam = useMemo(() => teams.find(t => t.id === selectedTeam), [teams, selectedTeam]);
  const currentCat = useMemo(() => cats.find(c => c.id === currentTeam?.category_id), [cats, currentTeam]);
  const currentPlayer = useMemo(() => players.find(p => p.id === activePlayer), [players, activePlayer]);
  const template = useMemo(() => {
    if (currentPlayer?.position === "POR") return porteroTemplate;
    return categoryTemplate;
  }, [currentPlayer, categoryTemplate, porteroTemplate]);

  const setRating = (indicatorId, rating) => {
    setPlayerStats((prev) => {
      const ps = { ...prev[activePlayer] };
      const existing = ps.ratings.find(r => r.indicator_id === indicatorId);
      if (existing) {
        ps.ratings = ps.ratings.map(r => r.indicator_id === indicatorId ? { ...r, rating } : r);
      } else {
        ps.ratings = [...ps.ratings, { indicator_id: indicatorId, rating, intention: null, success: null }];
      }
      return { ...prev, [activePlayer]: ps };
    });
  };
  const setIntention = (indicatorId, val) => {
    setPlayerStats((prev) => {
      const ps = { ...prev[activePlayer] };
      const existing = ps.ratings.find(r => r.indicator_id === indicatorId);
      if (existing) ps.ratings = ps.ratings.map(r => r.indicator_id === indicatorId ? { ...r, intention: val } : r);
      else ps.ratings = [...ps.ratings, { indicator_id: indicatorId, rating: 0, intention: val, success: null }];
      return { ...prev, [activePlayer]: ps };
    });
  };
  const setSuccess = (indicatorId, val) => {
    setPlayerStats((prev) => {
      const ps = { ...prev[activePlayer] };
      const existing = ps.ratings.find(r => r.indicator_id === indicatorId);
      if (existing) ps.ratings = ps.ratings.map(r => r.indicator_id === indicatorId ? { ...r, success: val } : r);
      else ps.ratings = [...ps.ratings, { indicator_id: indicatorId, rating: 0, intention: null, success: val }];
      return { ...prev, [activePlayer]: ps };
    });
  };

  const getRating = (indId) => playerStats[activePlayer]?.ratings.find(r => r.indicator_id === indId);

  const updatePlayerField = (pid, field, value) => {
    setPlayerStats((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: value } }));
  };

  // Save as draft (`in progress`, doesn't count in stats) or as final.
  // Called either from the form submit (final) or from the "Guardar borrador" button (draft).
  const saveJornada = async (newStatus) => {
    if (!selectedTeam) {
      toast.error("Selecciona un equipo primero");
      return;
    }
    const payload = {
      team_id: selectedTeam,
      session_type: form.session_type,
      date: form.date,
      opponent: form.session_type === "partido" ? form.opponent : null,
      home_away: form.session_type === "partido" ? form.home_away : null,
      jornada_number: form.session_type === "partido" ? Number(form.jornada_number) : null,
      team_stats: form.session_type === "partido" ? {
        gols_favor: Number(form.team_stats.gols_favor),
        gols_contra: Number(form.team_stats.gols_contra),
        gols_propia: Number(form.team_stats.gols_propia),
        xuts_favor: Number(form.team_stats.xuts_favor),
        xuts_contra: Number(form.team_stats.xuts_contra),
        parades_porter: Number(form.team_stats.parades_porter),
        targetes_grogues: Number(form.team_stats.targetes_grogues),
        targetes_roges: Number(form.team_stats.targetes_roges),
        // Advanced stats (iter 39)
        corners_favor: Number(form.team_stats.corners_favor || 0),
        corners_contra: Number(form.team_stats.corners_contra || 0),
        tiros_bloqueados: Number(form.team_stats.tiros_bloqueados || 0),
        duelos_aereos_ganados: Number(form.team_stats.duelos_aereos_ganados || 0),
        duelos_aereos_perdidos: Number(form.team_stats.duelos_aereos_perdidos || 0),
        pases_completados: Number(form.team_stats.pases_completados || 0),
        pases_intentados: Number(form.team_stats.pases_intentados || 0),
        // Auto-agregado: FdJ del equipo = suma de todos los FdJ de los jugadores.
        fueras_juego: Object.values(playerStats).reduce(
          (acc, ps) => acc + ((ps.offsides_detail || []).length), 0
        ),
        resultado: form.team_stats.resultado,
      } : null,
      player_stats: Object.values(playerStats).map((ps) => ({
        ...ps,
        minutos: Number(ps.minutos || 0),
        gols: Number(ps.gols || 0),
        asistencias: Number(ps.asistencias || 0),
        targetes_grogues: Number(ps.targetes_grogues || 0),
        targetes_roges: Number(ps.targetes_roges || 0),
        parades: Number(ps.parades || 0),
        shots_on_goal: Number(ps.shots_on_goal || 0),
        faltas_cometidas: Number(ps.faltas_cometidas || 0),
        faltas_recibidas: Number(ps.faltas_recibidas || 0),
        ratings: [],
        goals_detail: (ps.goals_detail || []).map(g => ({
          minute: g.minute === null || g.minute === "" || g.minute === undefined ? null : Number(g.minute),
          type: g.type || null,
          body_part: g.body_part || null,
        })),
        offsides_detail: (ps.offsides_detail || []).map(o => ({
          minute: o.minute === null || o.minute === "" || o.minute === undefined ? null : Number(o.minute),
        })),
        // Only forward the injury object when the flag is on — avoids creating empty stubs.
        injury: ps.injury && ps.injury.happened ? {
          happened: true,
          minute: ps.injury.minute === null || ps.injury.minute === "" || ps.injury.minute === undefined ? null : Number(ps.injury.minute),
          type: ps.injury.type || "otro",
          severity: ps.injury.severity || "leve",
          description: ps.injury.description || null,
        } : null,
      })),
      substitutions: (form.substitutions || [])
        .filter(s => s.out_player_id && s.in_player_id)
        .map(s => ({ minute: Number(s.minute || 0), out_player_id: s.out_player_id, in_player_id: s.in_player_id })),
      notes: form.notes || null,
      status: newStatus,
    };
    try {
      let jornadaId = editId;
      if (editId) {
        await api.put(`/jornadas/${editId}`, payload);
      } else {
        const { data } = await api.post("/jornadas", payload);
        jornadaId = data.id;
      }
      if (newStatus === "draft") {
        setForm(f => ({ ...f, status: "draft" }));
        toast.success("Borrador guardado. Puedes seguir editando.");
        // Stay on the same page but switch to edit mode so subsequent saves update in place.
        if (!editId && jornadaId) {
          navigate(`/coach/jornadas/new?edit=${jornadaId}`, { replace: true });
        }
      } else {
        // Determine correct message: closing a draft vs saving a normal jornada.
        const wasDraft = form.status === "draft";
        toast.success(wasDraft ? "Jornada cerrada" : (editId ? "Jornada actualizada" : "Jornada registrada"));
        navigate("/coach/jornadas");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    return saveJornada("final");
  };

  return (
    <div>
      <Link to="/coach/jornadas" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4 link-underline">
        <ArrowLeft size={14} /> Volver a jornadas
      </Link>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2">
            <span>Registrar</span>
            {form.status === "draft" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-primary bg-primary/10 text-primary text-[10px] font-heading tracking-widest animate-pulse" data-testid="draft-badge">
                <Radio size={10} /> BORRADOR
              </span>
            )}
          </div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">
            {editId ? "Editar jornada" : "Nueva jornada"}
          </h1>
          {currentTeam && (
            <div className="mt-2 text-muted-foreground text-sm">
              {currentCat?.name} · {currentTeam.name} · plantilla <span className="font-mono text-primary">{currentCat?.template_key}</span>
              {currentPlayer?.position === "POR" && <span className="ml-2 px-2 py-0.5 border border-primary text-primary font-mono text-[10px]">PORTERO</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!editId && (
            <Link to="/coach/pizarra" data-testid="jr-pizarra-link" className="h-10 px-4 border-2 border-emerald-500 text-emerald-400 inline-flex items-center gap-2 font-heading font-black uppercase text-[10px] tracking-wider hover:bg-emerald-500/10">
              ⚽ Partido en directo → Pizarra táctil
            </Link>
          )}
          <button
            type="button"
            data-testid="save-draft-btn"
            onClick={() => saveJornada("draft")}
            className="h-11 px-4 border-2 border-primary/60 hover:border-primary hover:bg-primary/10 text-primary text-xs uppercase font-heading tracking-widest flex items-center gap-2"
          >
            <Save size={14} /> Guardar borrador
          </button>
          <button
            type="button"
            data-testid="close-jornada-btn"
            onClick={() => saveJornada("final")}
            className="btn-volt"
          >
            <CheckCircle size={16} /> {form.status === "draft" ? "Cerrar jornada" : (editId ? "Guardar cambios" : "Guardar jornada")}
          </button>
        </div>
      </div>

      {/* EN VIVO banner: draft + date is today */}
      {form.status === "draft" && form.date === new Date().toISOString().slice(0, 10) && (
        <div className="mb-6 border border-primary bg-primary/5 p-4 flex items-center gap-3 flex-wrap" data-testid="live-banner">
          <div className="w-10 h-10 border border-primary flex items-center justify-center relative">
            <span className="absolute inset-0 border-2 border-primary animate-ping opacity-40"></span>
            <Radio size={16} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-black uppercase tracking-tight text-primary">Jornada EN VIVO</div>
            <div className="text-xs font-mono text-muted-foreground mt-0.5">
              Como el acta del árbitro. Guarda borrador durante el partido y ciérrala al final. Las estadísticas solo cuentan cuando cierras la jornada.
            </div>
          </div>
        </div>
      )}

      {form.session_type === "partido" && players.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="auto-convocatoria-btn"
            onClick={async () => {
              try {
                const { data } = await api.get(`/teams/${selectedTeam}/auto-convocatoria?limit=14&rotation=${rotate ? "true" : "false"}`);
                const ids = new Set(data.suggested.map(p => p.id));
                setPlayerStats((prev) => {
                  const next = { ...prev };
                  Object.keys(next).forEach(pid => { next[pid] = { ...next[pid], convocado: ids.has(pid) }; });
                  return next;
                });
                toast.success(
                  rotate
                    ? `Convocatoria rotada: ${data.suggested.length} jugadores (prioriza menos minutos)`
                    : `Convocatoria sugerida: ${data.suggested.length} jugadores`
                );
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }}
            className="inline-flex items-center gap-2 px-4 h-9 border border-primary/60 text-xs uppercase font-heading tracking-wider text-primary hover:bg-primary/10 transition-colors"
          >
            <Sparkles size={13} /> Sugerir convocatoria automática
          </button>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="rotation-toggle-label">
            <input
              type="checkbox"
              data-testid="rotation-toggle"
              checked={rotate}
              onChange={(e) => setRotate(e.target.checked)}
              className="accent-primary"
            />
            <span className="overline text-[10px]">Rotar según minutos jugados</span>
          </label>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6">
        {/* Session meta */}
        <div className="border border-border p-5">
          <div className="overline mb-4">Datos de la jornada</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SelectField label="Equipo" value={selectedTeam} onChange={setSelectedTeam} tid="jornada-team-select" disabled={!!editId}>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </SelectField>
            <SelectField label="Tipo" value={form.session_type} onChange={(v) => setForm({ ...form, session_type: v })} tid="jornada-type-select">
              <option value="partido">Partido</option>
              <option value="entreno">Entreno</option>
            </SelectField>
            <div>
              <label className="overline block mb-1">Fecha</label>
              <DateField value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="jornada-date-input" />
              {activeSeason && (
                <div className="text-[10px] font-mono mt-1" data-testid="jornada-season-badge">
                  {form.date >= activeSeason.start_date && form.date <= activeSeason.end_date ? (
                    <span className="text-primary">Temporada: <b>{activeSeason.label}</b> (activa)</span>
                  ) : (
                    <span className="text-amber-500">⚠ Fecha fuera de la temporada activa ({activeSeason.label})</span>
                  )}
                </div>
              )}
            </div>
            {form.session_type === "partido" && (
              <TextField label="Nº Jornada" type="number" value={form.jornada_number} onChange={(v) => setForm({ ...form, jornada_number: v })} tid="jornada-num-input" />
            )}
          </div>
          {form.session_type === "partido" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <TextField label="Rival" value={form.opponent} onChange={(v) => setForm({ ...form, opponent: v })} tid="jornada-opp-input" />
              <SelectField label="Local/Visitante" value={form.home_away} onChange={(v) => setForm({ ...form, home_away: v })} tid="jornada-ha-select">
                <option value="casa">Casa</option>
                <option value="fora">Fuera</option>
              </SelectField>
              <SelectField label="Resultado" value={form.team_stats.resultado} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, resultado: v } })} tid="jornada-res-select">
                <option value="V">Victoria</option>
                <option value="E">Empate</option>
                <option value="D">Derrota</option>
              </SelectField>
            </div>
          )}
        </div>

        {/* Team stats */}
        {form.session_type === "partido" && (
          <div className="border border-border p-5">
            <div className="overline mb-4">Estadísticas de equipo</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <NumField label="Goles a favor" value={form.team_stats.gols_favor} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, gols_favor: v } })} tid="ts-gf" />
              <NumField label="Goles en contra" value={form.team_stats.gols_contra} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, gols_contra: v } })} tid="ts-gc" />
              <NumField label="Tiros a favor" value={form.team_stats.xuts_favor} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, xuts_favor: v } })} tid="ts-xf" />
              <NumField label="Tiros en contra" value={form.team_stats.xuts_contra} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, xuts_contra: v } })} tid="ts-xc" />
              <NumField label="Paradas portero" value={form.team_stats.parades_porter} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, parades_porter: v } })} tid="ts-par" />
              <NumField label="G. propia" value={form.team_stats.gols_propia} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, gols_propia: v } })} tid="ts-gp" />
              <NumField label="T. amarillas" value={form.team_stats.targetes_grogues} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, targetes_grogues: v } })} tid="ts-tg" />
              <NumField label="T. rojas" value={form.team_stats.targetes_roges} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, targetes_roges: v } })} tid="ts-tr" />
            </div>

            {/* Advanced tactical stats — iter 39 */}
            <div className="overline mt-6 mb-4 text-muted-foreground">Estadísticas avanzadas <span className="text-[9px] normal-case tracking-normal opacity-60">(opcionales)</span></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <NumField label="Córners a favor" value={form.team_stats.corners_favor} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, corners_favor: v } })} tid="ts-cornerf" />
              <NumField label="Córners en contra" value={form.team_stats.corners_contra} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, corners_contra: v } })} tid="ts-cornerc" />
              <NumField label="Tiros bloqueados" value={form.team_stats.tiros_bloqueados} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, tiros_bloqueados: v } })} tid="ts-blocks" />
              <NumField label="Duelos aéreos ganados" value={form.team_stats.duelos_aereos_ganados} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, duelos_aereos_ganados: v } })} tid="ts-aer-g" />
              <NumField label="Duelos aéreos perdidos" value={form.team_stats.duelos_aereos_perdidos} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, duelos_aereos_perdidos: v } })} tid="ts-aer-p" />
              <NumField label="Pases completados" value={form.team_stats.pases_completados} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, pases_completados: v } })} tid="ts-pass-ok" />
              <NumField label="Pases intentados" value={form.team_stats.pases_intentados} onChange={(v) => setForm({ ...form, team_stats: { ...form.team_stats, pases_intentados: v } })} tid="ts-pass-att" />
              {/* Live % completion */}
              <div className="flex flex-col justify-center border border-primary/30 bg-primary/5 px-3 py-2" data-testid="ts-pass-pct">
                <div className="overline text-primary">% Pases</div>
                <div className="font-heading text-2xl font-black text-primary">
                  {(() => {
                    const ok = Number(form.team_stats.pases_completados || 0);
                    const att = Number(form.team_stats.pases_intentados || 0);
                    return att > 0 ? `${Math.round((ok / att) * 100)}%` : "—";
                  })()}
                </div>
              </div>
            </div>
            {/* Auto-computed: total fueras de juego = suma por jugador */}
            <div className="mt-4 border border-primary/30 bg-primary/5 p-3 flex items-center justify-between" data-testid="ts-fueras-juego-summary">
              <div>
                <div className="overline text-primary">Fueras de juego (total)</div>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">Se calcula automáticamente sumando los FdJ registrados por jugador</div>
              </div>
              <div className="font-heading text-3xl font-black text-primary" data-testid="ts-fueras-juego-total">
                {Object.values(playerStats).reduce((acc, ps) => acc + ((ps.offsides_detail || []).length), 0)}
              </div>
            </div>
          </div>
        )}

        {/* Substitutions — only for partido */}
        {form.session_type === "partido" && (
          <div className="border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Cambios en partido</div>
                <div className="text-xs text-muted-foreground mt-1">Selecciona por dorsal — se guarda vinculado al jugador.</div>
              </div>
              <button type="button" data-testid="add-sub-btn"
                onClick={() => setForm(f => ({ ...f, substitutions: [...(f.substitutions || []), { minute: 0, out_player_id: "", in_player_id: "" }] }))}
                className="inline-flex items-center gap-2 px-3 h-9 border border-primary text-primary hover:bg-primary/10 text-xs uppercase font-heading tracking-wider">
                <Plus size={13} /> Añadir cambio
              </button>
            </div>
            {(form.substitutions || []).length === 0 && (
              <div className="text-center py-6 border border-dashed border-border text-xs text-muted-foreground">Sin cambios registrados. Pulsa &quot;Añadir cambio&quot;.</div>
            )}
            <div className="space-y-2">
              {(form.substitutions || []).map((s, idx) => (
                <div key={idx} data-testid={`sub-row-${idx}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border border-border/60 p-3">
                  <div className="md:col-span-2">
                    <label className="overline block mb-1">Min</label>
                    <input type="number" min="0" max="120" data-testid={`sub-min-${idx}`}
                      value={s.minute} onChange={(e) => setForm(f => ({ ...f, substitutions: f.substitutions.map((x, i) => i === idx ? { ...x, minute: e.target.value } : x) }))}
                      className="w-full h-9 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
                  </div>
                  <div className="md:col-span-4">
                    <label className="overline block mb-1">Sale nº</label>
                    <select data-testid={`sub-out-${idx}`} value={s.out_player_id || ""}
                      onChange={(e) => setForm(f => ({ ...f, substitutions: f.substitutions.map((x, i) => i === idx ? { ...x, out_player_id: e.target.value } : x) }))}
                      className="w-full h-9 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                      <option value="">— dorsal —</option>
                      {players.filter(p => p.id !== s.in_player_id).map(p => (
                        <option key={p.id} value={p.id}>#{p.jersey_number ?? "?"} · {p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-4">
                    <label className="overline block mb-1">Entra nº</label>
                    <select data-testid={`sub-in-${idx}`} value={s.in_player_id || ""}
                      onChange={(e) => setForm(f => ({ ...f, substitutions: f.substitutions.map((x, i) => i === idx ? { ...x, in_player_id: e.target.value } : x) }))}
                      className="w-full h-9 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                      <option value="">— dorsal —</option>
                      {players.filter(p => p.id !== s.out_player_id).map(p => (
                        <option key={p.id} value={p.id}>#{p.jersey_number ?? "?"} · {p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <button type="button" data-testid={`sub-del-${idx}`}
                      onClick={() => setForm(f => ({ ...f, substitutions: f.substitutions.filter((_, i) => i !== idx) }))}
                      className="text-muted-foreground hover:text-destructive p-2">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Player selector + per-player stats (ratings removed → moved to /evaluations) */}
        <div className="border border-border">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="overline">Estadísticas por jugador</div>
            <Link to="/evaluations" className="text-xs uppercase font-heading tracking-wider text-primary hover:underline">
              Valoraciones técnicas → Evaluación
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4">
            {/* Player list */}
            <div className="lg:col-span-1 border-r border-border max-h-[600px] overflow-y-auto">
              {players.map((p) => {
                const ps = playerStats[p.id] || {};
                return (
                  <button
                    type="button"
                    key={p.id}
                    data-testid={`player-tab-${p.id}`}
                    onClick={() => setActivePlayer(p.id)}
                    className={`w-full text-left p-3 border-b border-border flex items-center gap-3 transition-colors ${activePlayer === p.id ? "bg-primary/10 border-l-2 border-l-primary" : "hover:bg-white/[0.02]"}`}
                  >
                    <div className="w-8 h-8 border border-border font-heading font-black text-sm flex items-center justify-center">{p.jersey_number ?? "?"}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-sm font-bold uppercase truncate">{p.name}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{p.position || "—"}</div>
                    </div>
                    {!ps.convocado && <span className="overline text-destructive">NC</span>}
                  </button>
                );
              })}
              {players.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">Sin jugadores</div>}
            </div>

            {/* Per-player stats — only match stats, no ratings */}
            <div className="lg:col-span-3 p-5">
              {activePlayer && playerStats[activePlayer] && (
                <>
                  {/* Sticky jornada context header — mirrors the main "Datos de la jornada" card
                      so the coach never loses sight of which match they're editing stats for. */}
                  <div className="border border-primary/40 bg-primary/5 p-3 mb-5 flex items-center gap-3 flex-wrap" data-testid="player-jornada-context">
                    <div className="w-9 h-9 border border-primary/60 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-heading font-black uppercase text-primary">
                        {form.session_type === "partido" ? (form.home_away === "casa" ? "L" : "V") : "E"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-heading font-black uppercase tracking-tight text-lg truncate">
                          {players.find(p => p.id === activePlayer)?.name}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          {players.find(p => p.id === activePlayer)?.position || ""}
                          {players.find(p => p.id === activePlayer)?.jersey_number ? ` · #${players.find(p => p.id === activePlayer).jersey_number}` : ""}
                        </span>
                      </div>
                      <div className="text-[11px] font-mono text-muted-foreground mt-0.5 flex items-center gap-x-3 gap-y-1 flex-wrap">
                        <span className="uppercase">{form.session_type}</span>
                        <span>· {form.date}</span>
                        {form.session_type === "partido" && (
                          <>
                            {form.jornada_number && <span>· J{form.jornada_number}</span>}
                            {form.opponent && <span>· vs <span className="text-foreground font-bold">{form.opponent}</span></span>}
                            <span>· {form.home_away === "casa" ? "Casa" : "Fuera"}</span>
                            {(form.team_stats?.gols_favor !== undefined || form.team_stats?.gols_contra !== undefined) && (
                              <span className={`px-1.5 py-0.5 border font-heading font-black text-[11px] ${form.team_stats?.resultado === "V" ? "border-emerald-500 text-emerald-400" : form.team_stats?.resultado === "D" ? "border-destructive text-destructive" : "border-border"}`}>
                                {form.home_away === "casa" ? `${form.team_stats?.gols_favor ?? 0}-${form.team_stats?.gols_contra ?? 0}` : `${form.team_stats?.gols_contra ?? 0}-${form.team_stats?.gols_favor ?? 0}`}
                                {form.team_stats?.resultado && <span className="ml-1 opacity-70">({form.team_stats.resultado})</span>}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <label className="flex items-center gap-2 text-sm">
                      <input data-testid="ps-convocado" type="checkbox" checked={playerStats[activePlayer].convocado} onChange={(e) => updatePlayerField(activePlayer, "convocado", e.target.checked)} className="accent-primary" />
                      Convocado
                    </label>
                    <NumField label="Minutos" value={playerStats[activePlayer].minutos} onChange={(v) => updatePlayerField(activePlayer, "minutos", v)} tid="ps-min" small />
                    <NumField label="Goles" value={playerStats[activePlayer].gols} onChange={(v) => updatePlayerField(activePlayer, "gols", v)} tid="ps-gols" small />
                    <NumField label="Asistencias" value={playerStats[activePlayer].asistencias} onChange={(v) => updatePlayerField(activePlayer, "asistencias", v)} tid="ps-ast" small />
                    <NumField label="Disparos a puerta" value={playerStats[activePlayer].shots_on_goal || 0} onChange={(v) => updatePlayerField(activePlayer, "shots_on_goal", v)} tid="ps-shots" small />
                    <NumField label="Faltas cometidas" value={playerStats[activePlayer].faltas_cometidas || 0} onChange={(v) => updatePlayerField(activePlayer, "faltas_cometidas", v)} tid="ps-fc" small />
                    <NumField label="Faltas recibidas" value={playerStats[activePlayer].faltas_recibidas || 0} onChange={(v) => updatePlayerField(activePlayer, "faltas_recibidas", v)} tid="ps-fr" small />
                    <NumField label="T. amarillas" value={playerStats[activePlayer].targetes_grogues} onChange={(v) => updatePlayerField(activePlayer, "targetes_grogues", v)} tid="ps-tg" small />
                    <NumField label="T. rojas" value={playerStats[activePlayer].targetes_roges} onChange={(v) => updatePlayerField(activePlayer, "targetes_roges", v)} tid="ps-tr" small />
                    <NumField label="Paradas" value={playerStats[activePlayer].parades} onChange={(v) => updatePlayerField(activePlayer, "parades", v)} tid="ps-par" small />
                  </div>

                  <div className="border border-dashed border-primary/40 bg-primary/5 p-4 mb-4">
                    <div className="flex items-center gap-2 text-xs text-primary uppercase font-heading tracking-wider">
                      <Layers size={13} /> Valoraciones técnicas
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
              Las valoraciones (Técnica/Táctica/Pies/Físico/Psicológico) ya no se registran por jornada. Se hacen en la sección <Link to="/evaluations" className="text-primary hover:underline">Evaluación</Link> con la frecuencia que decida cada club.
                    </p>
                  </div>

                  <div className="mt-4">
                    <label className="overline block mb-1">Observaciones del entrenador</label>
                    <textarea
                      data-testid="ps-observ"
                      value={playerStats[activePlayer].observaciones || ""}
                      onChange={(e) => updatePlayerField(activePlayer, "observaciones", e.target.value)}
                      rows={3}
                      placeholder="Añade notas específicas del jugador…"
                      className="w-full bg-transparent border border-border p-3 font-mono text-sm focus:border-primary focus:outline-none"
                    />
                  </div>

                  {/* GOLES DETALLADOS — minuto + tipo + parte del cuerpo */}
                  {form.session_type === "partido" && (
                    <GoalsBlock
                      goals={playerStats[activePlayer].goals_detail || []}
                      onChange={(next) => {
                        updatePlayerField(activePlayer, "goals_detail", next);
                        // Keep the simple gols counter in sync when using detailed goals.
                        if (next.length > 0) {
                          updatePlayerField(activePlayer, "gols", next.length);
                        }
                      }}
                    />
                  )}

                  {/* FUERAS DE JUEGO — minuto por cada uno; se agrega automáticamente al team_stats.fueras_juego */}
                  {form.session_type === "partido" && (
                    <OffsidesBlock
                      offsides={playerStats[activePlayer].offsides_detail || []}
                      onChange={(next) => updatePlayerField(activePlayer, "offsides_detail", next)}
                    />
                  )}

                  {/* LESIÓN — solo se materializa al cerrar la jornada */}
                  {form.session_type === "partido" && (
                    <InjuryBlock
                      value={playerStats[activePlayer].injury || { happened: false }}
                      onChange={(next) => updatePlayerField(activePlayer, "injury", next)}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="overline block mb-2">Notas generales de la sesión</label>
          <textarea data-testid="jornada-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
            className="w-full bg-transparent border border-border p-3 font-mono text-sm focus:border-primary focus:outline-none"
            placeholder="Observaciones, incidencias, aprendizajes…" />
        </div>

        {/* mb-28: keep the submit clear of the floating GHL chat bubble (bottom-right) */}
        <div className="flex justify-end gap-3 flex-wrap mb-28 md:pr-24">
          <button type="button" data-testid="save-draft-btn-bottom" onClick={() => saveJornada("draft")} className="h-11 px-4 border-2 border-primary/60 hover:border-primary hover:bg-primary/10 text-primary text-xs uppercase font-heading tracking-widest flex items-center gap-2">
            <Save size={14} /> Guardar borrador
          </button>
          <button type="submit" data-testid="save-jornada-btn" className="btn-volt">
            <CheckCircle size={16} /> {form.status === "draft" ? "Cerrar jornada" : (editId ? "Guardar cambios" : "Guardar jornada")}
          </button>
        </div>
      </form>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", tid }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      <input type={type} data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
    </div>
  );
}
function NumField({ label, value, onChange, tid, small }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      <input type="number" data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)}
        className={`w-full ${small ? "h-9" : "h-10"} bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none`} />
    </div>
  );
}
function SelectField({ label, value, onChange, children, tid, disabled }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      <select disabled={disabled} data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none disabled:opacity-60">
        {children}
      </select>
    </div>
  );
}

const GOAL_TYPES = [
  { value: "jugada", label: "De jugada" },
  { value: "corner", label: "Córner / ABP" },
  { value: "penalti", label: "Penalti" },
  { value: "falta_directa", label: "Falta directa" },
  { value: "propia", label: "En propia" },
];
const GOAL_BODY_PARTS = [
  { value: "pie_derecho", label: "Pie derecho" },
  { value: "pie_izquierdo", label: "Pie izquierdo" },
  { value: "cabeza", label: "Cabeza" },
  { value: "otro", label: "Otro" },
];

function OffsidesBlock({ offsides, onChange }) {
  const add = () => onChange([...offsides, { minute: null }]);
  const upd = (i, k, v) => onChange(offsides.map((o, ix) => ix === i ? { ...o, [k]: v } : o));
  const del = (i) => onChange(offsides.filter((_, ix) => ix !== i));
  return (
    <div className="mt-4 border border-border p-4" data-testid="offsides-block">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline">Fueras de juego del jugador</div>
          <div className="text-[10px] font-mono text-muted-foreground mt-1">
            {offsides.length} fuera{offsides.length === 1 ? "" : "s"} de juego · registra minuto de cada uno
          </div>
        </div>
        <button type="button" data-testid="add-offside-btn" onClick={add} className="h-9 px-3 border border-primary text-primary hover:bg-primary/10 text-xs uppercase font-heading tracking-widest inline-flex items-center gap-2">
          <Plus size={13} /> Añadir fuera de juego
        </button>
      </div>
      {offsides.length === 0 ? (
        <div className="text-xs text-muted-foreground text-center py-3">Sin fueras de juego. Pulsa &quot;Añadir fuera de juego&quot; para registrar cada uno.</div>
      ) : (
        <div className="space-y-2">
          {offsides.map((o, i) => (
            <div key={i} data-testid={`offside-row-${i}`} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-3">
                <label className="overline block mb-1">Min.</label>
                <input type="number" data-testid={`offside-min-${i}`} value={o.minute ?? ""} placeholder="—" onChange={(e) => upd(i, "minute", e.target.value === "" ? null : Number(e.target.value))} className="w-full h-9 bg-transparent border border-border px-2 font-mono text-sm focus:border-primary focus:outline-none" />
              </div>
              <div className="col-span-8 flex items-center text-xs text-muted-foreground pl-2">
                Fuera de juego #{i + 1}
              </div>
              <div className="col-span-1 flex justify-end">
                <button type="button" data-testid={`offside-del-${i}`} onClick={() => del(i)} className="h-9 w-9 border border-border hover:border-destructive hover:text-destructive flex items-center justify-center">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GoalsBlock({ goals, onChange }) {
  const add = () => onChange([...goals, { minute: null, type: "jugada", body_part: "pie_derecho" }]);
  const upd = (i, k, v) => onChange(goals.map((g, ix) => ix === i ? { ...g, [k]: v } : g));
  const del = (i) => onChange(goals.filter((_, ix) => ix !== i));
  return (
    <div className="mt-4 border border-border p-4" data-testid="goals-block">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline">Goles del jugador</div>
          <div className="text-[10px] font-mono text-muted-foreground mt-1">
            {goals.length} gol{goals.length === 1 ? "" : "es"} · minuto, tipo y parte del cuerpo
          </div>
        </div>
        <button type="button" data-testid="add-goal-btn" onClick={add} className="h-9 px-3 border border-primary text-primary hover:bg-primary/10 text-xs uppercase font-heading tracking-widest inline-flex items-center gap-2">
          <Plus size={13} /> Añadir gol
        </button>
      </div>
      {goals.length === 0 ? (
        <div className="text-xs text-muted-foreground text-center py-3">Sin goles registrados. Pulsa &quot;Añadir gol&quot; para detallar cada uno.</div>
      ) : (
        <div className="space-y-2">
          {goals.map((g, i) => (
            <div key={i} data-testid={`goal-row-${i}`} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-2">
                <label className="overline block mb-1">Min.</label>
                <input type="number" data-testid={`goal-min-${i}`} value={g.minute ?? ""} placeholder="—" onChange={(e) => upd(i, "minute", e.target.value === "" ? null : Number(e.target.value))} className="w-full h-9 bg-transparent border border-border px-2 font-mono text-sm focus:border-primary focus:outline-none" />
              </div>
              <div className="col-span-5">
                <label className="overline block mb-1">Tipo</label>
                <select data-testid={`goal-type-${i}`} value={g.type || ""} onChange={(e) => upd(i, "type", e.target.value)} className="w-full h-9 bg-card border border-border px-2 font-mono text-sm focus:border-primary focus:outline-none">
                  {GOAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="col-span-4">
                <label className="overline block mb-1">Con</label>
                <select data-testid={`goal-part-${i}`} value={g.body_part || ""} onChange={(e) => upd(i, "body_part", e.target.value)} className="w-full h-9 bg-card border border-border px-2 font-mono text-sm focus:border-primary focus:outline-none">
                  {GOAL_BODY_PARTS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="col-span-1 flex justify-end">
                <button type="button" data-testid={`goal-del-${i}`} onClick={() => del(i)} className="h-9 w-9 border border-border hover:border-destructive hover:text-destructive flex items-center justify-center">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const INJURY_TYPES = [
  { value: "muscular", label: "Muscular" },
  { value: "articular", label: "Articular" },
  { value: "osea", label: "Ósea / fractura" },
  { value: "contusion", label: "Contusión / golpe" },
  { value: "otro", label: "Otro" },
];
const INJURY_SEVERITIES = [
  { value: "leve", label: "Leve" },
  { value: "moderada", label: "Moderada" },
  { value: "grave", label: "Grave" },
];

function InjuryBlock({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const isOn = !!value.happened;
  return (
    <div className={`mt-4 border p-4 ${isOn ? "border-destructive/70 bg-destructive/5" : "border-border"}`} data-testid="injury-block">
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" data-testid="injury-happened" checked={isOn} onChange={(e) => set("happened", e.target.checked)} className="accent-primary" />
        <span className="font-heading font-bold uppercase tracking-tight text-sm">
          ¿Se ha lesionado el jugador?
        </span>
      </label>
      {isOn && (
        <>
          <div className="text-[11px] font-mono text-muted-foreground mt-2 mb-3">
            Al cerrar la jornada se creará automáticamente una lesión activa en <b>Lesiones</b>.
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <NumField label="Minuto" value={value.minute ?? ""} onChange={(v) => set("minute", v === "" ? null : Number(v))} tid="injury-min" small />
            <div>
              <label className="overline block mb-1">Tipo</label>
              <select data-testid="injury-type" value={value.type || "otro"} onChange={(e) => set("type", e.target.value)} className="w-full h-9 bg-card border border-border px-2 font-mono text-sm focus:border-primary focus:outline-none">
                {INJURY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1">Gravedad</label>
              <select data-testid="injury-severity" value={value.severity || "leve"} onChange={(e) => set("severity", e.target.value)} className="w-full h-9 bg-card border border-border px-2 font-mono text-sm focus:border-primary focus:outline-none">
                {INJURY_SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="overline block mb-1">Descripción breve</label>
            <input type="text" data-testid="injury-desc" value={value.description || ""} onChange={(e) => set("description", e.target.value)} placeholder="Ej: molestias en isquiotibiales pierna dcha" className="w-full h-9 bg-transparent border border-border px-2 font-mono text-sm focus:border-primary focus:outline-none" />
          </div>
        </>
      )}
    </div>
  );
}

